import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, XCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Checkbox } from '../components/ui/Checkbox';
import { EmptyState } from '../components/ui/EmptyState';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useLeaveTypes } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type LeaveApp = { id: string; leaveTypeId: string; startDate: string; endDate: string; days: string; halfDayPeriod: string | null; reason: string; status: string; reviewNote: string | null; createdAt: string };
const daysLabel = (leave: Pick<LeaveApp, 'days' | 'halfDayPeriod'>) => `${leave.days} day(s)${leave.halfDayPeriod ? ` (${leave.halfDayPeriod === 'first_half' ? 'first half' : 'second half'})` : ''}`;
type LeaveBalance = { leaveTypeId: string; year: number; entitled: string; used: string; carriedForward: string };

const STATUS_STYLE: Record<string, string> = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-brand-100 text-brand-700', rejected: 'bg-red-50 text-red-700', cancelled: 'bg-slate-100 text-slate-600' };
const raisedAt = (v: string) => new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' });

function BalancesStrip() {
  const leaveTypes = useLeaveTypes();
  const year = new Date().getFullYear();
  const balances = useQuery({ queryKey: ['leave-balances', year], queryFn: async () => (await api.get<LeaveBalance[]>('/api/leave-balances', { params: { year } })).data });
  const byType = new Map((balances.data ?? []).map((b) => [b.leaveTypeId, b]));
  if (leaveTypes.isLoading) return null;
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-3">
      {leaveTypes.data?.filter((t) => t.isActive).map((type) => {
        const b = byType.get(type.id);
        const remaining = b ? (Number(b.entitled) + Number(b.carriedForward) - Number(b.used)).toFixed(1) : '—';
        return (
          <div key={type.id} className="card">
            <p className="text-sm text-muted">{type.name}</p>
            <p className="mt-1 text-2xl font-bold text-brand-700">{remaining}</p>
            <p className="mt-0.5 text-xs text-muted">of {b?.entitled ?? type.yearlyEntitlement} days</p>
          </div>
        );
      })}
    </div>
  );
}

function ApplyForm({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const leaveTypes = useLeaveTypes();
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [halfDay, setHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<'first_half' | 'second_half'>('first_half');
  const [reason, setReason] = useState('');

  // Half-day leave is always exactly one day — the end-date field is hidden once it's on,
  // and picking a start date after already checking it keeps them in sync automatically.
  const days = halfDay ? (startDate ? '0.5' : '') : startDate && endDate && endDate >= startDate ? (Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000) + 1).toString() : '';

  const apply = useMutation({
    mutationFn: () => api.post('/api/leave-applications', { leaveTypeId, startDate, endDate: halfDay ? startDate : endDate, days, reason, ...(halfDay ? { halfDayPeriod } : {}) }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-applications'] }); success('Leave application submitted'); onDone(); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not submit application')),
  });

  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); apply.mutate(); }}>
      <label className="label">Leave type<Select className="mt-1" required value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)}><option value="">Select…</option>{leaveTypes.data?.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</Select></label>
      <label className="label">
        {halfDay ? 'Date' : 'Days'}{days && ` (${days})`}
        <div className="mt-1 grid grid-cols-2 gap-2">
          <input type="date" className="field" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          {!halfDay && <input type="date" className="field" required min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} />}
        </div>
      </label>
      <div className="sm:col-span-2"><Checkbox checked={halfDay} onChange={setHalfDay} label="Half day" /></div>
      {halfDay && <label className="label sm:col-span-2">Which half?<Select className="mt-1" value={halfDayPeriod} onChange={(e) => setHalfDayPeriod(e.target.value as 'first_half' | 'second_half')}><option value="first_half">First half</option><option value="second_half">Second half</option></Select></label>}
      <label className="label sm:col-span-2">Reason<textarea className="field mt-1 min-h-24 py-2" required value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={apply.isPending}>Submit</button>
        <button type="button" className="btn-secondary" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

export function LeavePage() {
  const client = useQueryClient();
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const leaveTypes = useLeaveTypes();
  const userId = user?.id ?? user?.sub;
  // Scoped to `userId` explicitly — without it, a manager's default scope on this endpoint
  // returns every staff member they're allowed to see, not just their own leave. This screen
  // is "my leave", full stop; the team-wide view lives on Team Status > Approvals instead.
  const applications = useQuery({ queryKey: ['leave-applications', 'mine', userId], queryFn: async () => (await api.get<LeaveApp[]>('/api/leave-applications', { params: { userId } })).data, enabled: !!userId });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/leave-applications/${id}/cancel`, {}),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-applications'] }); void client.invalidateQueries({ queryKey: ['leave-balances'] }); success('Application cancelled'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not cancel')),
  });
  const typeName = (id: string) => leaveTypes.data?.find((t) => t.id === id)?.name ?? 'Leave';
  const rows = applications.data ?? [];

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Leave</h1><p className="mt-1 text-muted">Apply and track your own leave.</p></div>
        <button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> Apply</button>
      </div>

      <BalancesStrip />
      {open && <ApplyForm onDone={() => setOpen(false)} />}

      {applications.isLoading && <Loader />}
      {!applications.isLoading && rows.length === 0 && <EmptyState title="No leave applications yet" />}

      <div className="space-y-3 lg:hidden">
        {rows.map((leave) => (
          <article key={leave.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <strong>{typeName(leave.leaveTypeId)}</strong>
                <p className="mt-1 text-sm text-muted">{leave.startDate === leave.endDate ? leave.startDate : `${leave.startDate} → ${leave.endDate}`} • {daysLabel(leave)}</p>
                <p className="mt-1 text-xs text-muted">Raised {raisedAt(leave.createdAt)}</p>
                <p className="mt-1 text-sm">{leave.reason}</p>
                {leave.reviewNote && <p className="mt-1 text-sm italic text-muted">"{leave.reviewNote}"</p>}
              </div>
              <span className={`badge shrink-0 ${STATUS_STYLE[leave.status] ?? ''}`}>{leave.status}</span>
            </div>
            {(leave.status === 'pending' || leave.status === 'approved') && (
              <button className="btn-secondary mt-3" disabled={cancel.isPending} onClick={() => { if (confirm('Cancel this leave application?')) cancel.mutate(leave.id); }}><XCircle size={16} /> Cancel</button>
            )}
          </article>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Type</th><th>Dates</th><th>Days</th><th>Raised at</th><th>Reason</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((leave) => (
                <tr key={leave.id}>
                  <td><strong>{typeName(leave.leaveTypeId)}</strong></td>
                  <td className="text-muted">{leave.startDate === leave.endDate ? leave.startDate : `${leave.startDate} → ${leave.endDate}`}</td>
                  <td className="text-muted">{daysLabel(leave)}</td>
                  <td className="text-muted">{raisedAt(leave.createdAt)}</td>
                  <td className="max-w-xs truncate text-muted">{leave.reason}{leave.reviewNote && <span className="ml-1 italic">— "{leave.reviewNote}"</span>}</td>
                  <td><span className={`badge ${STATUS_STYLE[leave.status] ?? ''}`}>{leave.status}</span></td>
                  <td>
                    {(leave.status === 'pending' || leave.status === 'approved') && (
                      <button className="btn-secondary !px-3" aria-label="Cancel" disabled={cancel.isPending} onClick={() => { if (confirm('Cancel this leave application?')) cancel.mutate(leave.id); }}><XCircle size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
